import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { ApiError } from '../api/client';
import { useDeleteSong, useRenameSong, useSongs } from '../api/songs';
import type { Song } from '../api/types';
import { useMembership } from '../auth/session';
import { PartChips } from '../components/Chips';
import { Dialog } from '../components/Dialog';
import { ErrorView } from '../components/Guards';
import { formatBpm, formatDate } from '../lib/format';
import { dateLocale } from '../i18n';
import ui from '../styles/ui.module.css';
import styles from './LibraryPage.module.css';

const iconProps = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, focusable: false } as const;

const EditIcon = () => (
  <svg {...iconProps}>
    <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);
const DeleteIcon = () => (
  <svg {...iconProps}>
    <path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13M10 11v5.5M14 11v5.5" />
  </svg>
);

/** Director home: the choir's song library, with the upload button, and renaming and deleting songs. */
export function LibraryPage() {
  const { t } = useTranslation();
  const membership = useMembership();
  const songs = useSongs(membership?.choirId);
  const uploaded = (useLocation().state as { uploadedTitle?: string } | null)?.uploadedTitle;
  const [editing, setEditing] = useState<Song | null>(null);
  const [deleting, setDeleting] = useState<Song | null>(null);
  const [deletedTitle, setDeletedTitle] = useState<string | null>(null);

  if (!membership) return null;

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('library.title')}</h1>
          <div className={ui.subtitle}>
            {t('library.subtitle', { choir: membership.choirName, pitch: membership.referencePitchHz })}
          </div>
        </div>
        <Link to="/library/upload" className={`${ui.button} ${ui.primary}`}>
          {t('library.upload')}
        </Link>
      </div>

      {deletedTitle && (
        <div className={`${ui.notice} ${ui.noticeSuccess}`} role="status" data-testid="deleted-notice">
          {t('library.deleted', { title: deletedTitle })}
        </div>
      )}
      {uploaded && !deletedTitle && (
        <div className={`${ui.notice} ${ui.noticeSuccess}`} role="status">
          {t('library.uploaded', { title: uploaded })}
        </div>
      )}

      <section className={ui.card}>
        {songs.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {songs.isError && <ErrorView onRetry={() => void songs.refetch()} />}
        {songs.data && songs.data.length === 0 && (
          <div className={ui.empty}>
            <p>{t('library.empty')}</p>
            <p>{t('library.emptyHint')}</p>
          </div>
        )}
        {songs.data && songs.data.length > 0 && (
          <table className={ui.table}>
            <thead>
              <tr>
                <th>{t('library.colSong')}</th>
                <th>{t('library.colParts')}</th>
                <th>{t('library.colBars')}</th>
                <th>{t('library.colTempo')}</th>
                <th>{t('library.colUploaded')}</th>
                <th aria-label={t('library.colActions')} />
              </tr>
            </thead>
            <tbody>
              {songs.data.map((song) => {
                const s = song.summary;
                return (
                  <tr key={song.id} data-testid="song-row">
                    <td>
                      <Link to={`/songs/${song.id}`}>{song.title}</Link>
                      {song.composer && <span className={ui.secondary}>{song.composer}</span>}
                    </td>
                    <td>
                      <PartChips summary={s} />
                    </td>
                    <td>
                      {t('library.barsCell', { count: s.measureCount })}
                      {s.playbackMeasureCount !== s.measureCount && (
                        <span className={ui.secondary}>{t('library.playbackCell', { count: s.playbackMeasureCount })}</span>
                      )}
                    </td>
                    <td>{s.tempo ? `♩ = ${formatBpm(s.tempo.quarterBpm)}` : '–'}</td>
                    <td>
                      {formatDate(song.createdAt, dateLocale())}
                      <span className={ui.secondary}>{song.uploadedByName ?? t('common.deletedUser')}</span>
                    </td>
                    <td className={styles.actions}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={t('library.edit', { title: song.title })}
                        data-tip={t('library.editTip')}
                        onClick={() => setEditing(song)}
                      >
                        <EditIcon />
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.danger}`}
                        aria-label={t('library.delete', { title: song.title })}
                        data-tip={t('library.deleteTip')}
                        onClick={() => setDeleting(song)}
                      >
                        <DeleteIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <EditSongDialog choirId={membership.choirId} song={editing} onClose={() => setEditing(null)} />
      <DeleteSongDialog
        choirId={membership.choirId}
        song={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(title) => {
          setDeleting(null);
          setDeletedTitle(title);
        }}
      />
    </div>
  );
}

function errorText(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  const code = error instanceof ApiError ? error.code : undefined;
  return t(`apiErrors.${code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') });
}

/** Rename: the title (required) and the composer. */
function EditSongDialog({ choirId, song, onClose }: { choirId: string; song: Song | null; onClose: () => void }) {
  const { t } = useTranslation();
  const rename = useRenameSong(choirId);
  const [title, setTitle] = useState('');
  const [composer, setComposer] = useState('');
  const [shownFor, setShownFor] = useState<string | null>(null);
  if (song && shownFor !== song.id) {
    setShownFor(song.id);
    setTitle(song.title);
    setComposer(song.composer ?? '');
    rename.reset();
  }

  function close() {
    setShownFor(null);
    onClose();
  }

  return (
    <Dialog open={!!song} title={t('library.editTitle')} onClose={close} data-testid="edit-song-dialog">
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!song || !title.trim()) return;
          rename.mutate({ songId: song.id, title, composer }, { onSuccess: close });
        }}
      >
        <label className={ui.field}>
          <span className={ui.label}>{t('upload.titleLabel')}</span>
          <input className={ui.input} value={title} maxLength={200} required onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className={ui.field}>
          <span className={ui.label}>{t('upload.composerLabel')}</span>
          <input className={ui.input} value={composer} maxLength={200} onChange={(e) => setComposer(e.target.value)} />
        </label>
        {rename.isError && <div className={`${ui.notice} ${ui.noticeError}`} role="alert">{errorText(rename.error, t)}</div>}
        <div className={styles.buttons}>
          <button type="button" className={ui.button} onClick={close}>
            {t('library.cancel')}
          </button>
          <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!title.trim() || rename.isPending}>
            {t('library.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Delete, after saying what goes with the song. */
function DeleteSongDialog({
  choirId,
  song,
  onClose,
  onDeleted,
}: {
  choirId: string;
  song: Song | null;
  onClose: () => void;
  onDeleted: (title: string) => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteSong(choirId);
  const [shownFor, setShownFor] = useState<string | null>(null);
  if (song && shownFor !== song.id) {
    setShownFor(song.id);
    remove.reset();
  }

  function close() {
    setShownFor(null);
    onClose();
  }

  return (
    <Dialog open={!!song} title={t('library.deleteTitle')} onClose={close} data-testid="delete-song-dialog">
      {song && (
        <>
          <p className={styles.noMargin}>{t('library.deleteQuestion', { title: song.title })}</p>
          <p className={styles.noMargin}>
            {song.submissionCount > 0
              ? t('library.deleteWithTakes', { count: song.submissionCount })
              : t('library.deleteNoTakes')}
          </p>
          <p className={`${styles.noMargin} ${ui.muted}`}>{t('library.deletePractice')}</p>
          {remove.isError && <div className={`${ui.notice} ${ui.noticeError}`} role="alert">{errorText(remove.error, t)}</div>}
          <div className={styles.buttons}>
            <button type="button" className={ui.button} onClick={close}>
              {t('library.cancel')}
            </button>
            <button
              type="button"
              className={`${ui.button} ${styles.dangerButton}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate(song.id, { onSuccess: () => onDeleted(song.title) })}
            >
              {t('library.deleteConfirm')}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
